import type React from 'react';
import { createIncomingHandler } from './createIncomingHandler';
import { flushSequencedBuffer } from './flushSequencedBuffer';
import { createReplayRequester } from './requestReplay';

type TokenGetter = () => string | undefined | Promise<string | undefined>;

type ChatSyncParams = {
  getUserToken: TokenGetter;
  unwrapMsgEnvelope: (payload: any) => any;
  mergeMessages: (current: any[], payload: any, eventName?: string) => any[];
  getChatBuffer: (chatId: string) => any;
  setChatBuffer: (chatId: string, buffer: any) => void;
  activeChatIdRef: React.MutableRefObject<string | null>;
  setHistory: React.Dispatch<React.SetStateAction<any[]>>;
  setClarificationsToSend: (value: any) => void;
};

type ChatSyncHandlers = {
  onMessageReceived?: (chatKey: string, payload: any, eventName?: string) => void;
  onPartialReceived?: (chatKey: string, payload: any) => void;
  onFinalReceived?: (chatKey: string, payload: any) => void;
};

export type ChatSyncConsumer = {
  getUserToken: TokenGetter;
  onIncoming: (chatKey: string, payload: any, eventName?: string) => void;
  clearState: (chatKey: string) => void;
};

export const createChatSyncConsumer = (params: ChatSyncParams, handlers?: ChatSyncHandlers): ChatSyncConsumer => {
  const handleIncomingRef = { current: (_: string, __: any, ___?: string) => undefined as any };
  const sequencedMessagesRef = { current: new Map() };
  const sequencedFlushTimersRef = { current: new Map() };
  const replayInFlightRef = { current: new Map() };
  const lastReceivedSeqRef = { current: new Map() };
  const missingSeqRef = { current: new Map() };
  const missingSeqRetriesRef = { current: new Map() };
  const missingSeqTimersRef = { current: new Map() };

  const requestReplay = createReplayRequester({
    getUserToken: params.getUserToken,
    replayInFlightRef,
    handleIncomingRef,
  });

  const flushSequencedBufferForChat = (chatId: string, state: any) => {
    flushSequencedBuffer(chatId, state, {
      sequencedFlushTimersRef,
      getChatBuffer: params.getChatBuffer,
      mergeMessages: params.mergeMessages,
      setChatBuffer: params.setChatBuffer,
      activeChatIdRef: params.activeChatIdRef,
      setHistory: params.setHistory,
    });
  };

  const inner = createIncomingHandler({
    unwrapMsgEnvelope: params.unwrapMsgEnvelope,
    mergeMessages: params.mergeMessages,
    getChatBuffer: params.getChatBuffer,
    setChatBuffer: params.setChatBuffer,
    activeChatIdRef: params.activeChatIdRef,
    setHistory: params.setHistory,
    setClarificationsToSend: params.setClarificationsToSend,
    sequencedMessagesRef,
    sequencedFlushTimersRef,
    lastReceivedSeqRef,
    missingSeqRef,
    missingSeqRetriesRef,
    missingSeqTimersRef,
    requestReplay,
    flushSequencedBuffer: flushSequencedBufferForChat,
  });

  const onIncoming = (chatKey: string, payload: any, eventName?: string) => {
    const lower = eventName?.toLowerCase() ?? '';
    if (handlers?.onMessageReceived) handlers.onMessageReceived(chatKey, payload, eventName);
    if (handlers?.onPartialReceived && lower.includes('partial')) handlers.onPartialReceived(chatKey, payload);
    if (handlers?.onFinalReceived && (payload?.isFinal || payload?.type === 'final' || lower.includes('assistantresponse'))) {
      handlers.onFinalReceived(chatKey, payload);
    }
    inner(chatKey, payload, eventName);
  };

  handleIncomingRef.current = onIncoming;

  const clearState = (id: string) => {
    sequencedMessagesRef.current.delete(id);
    replayInFlightRef.current.delete(id);
    lastReceivedSeqRef.current.delete(id);
    missingSeqRef.current.delete(id);
    missingSeqRetriesRef.current.delete(id);
    const missingTimer = missingSeqTimersRef.current.get(id);
    if (missingTimer) {
      clearTimeout(missingTimer);
      missingSeqTimersRef.current.delete(id);
    }
    const timerId = sequencedFlushTimersRef.current.get(id);
    if (timerId) {
      clearTimeout(timerId);
      sequencedFlushTimersRef.current.delete(id);
    }
  };

  return {
    getUserToken: async () => params.getUserToken(),
    onIncoming,
    clearState,
  };
};
